import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaNewResidentRegistrationPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">New Resident Registration</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">New Resident Vehicle Registration in Nevada</h1>
        <p className="text-obsidian-300">Just moved to Nevada? Here&apos;s everything you need to know about registering your vehicle as a new resident.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <p className="text-amber-800 font-bold text-sm">30-Day Deadline</p>
          <p className="text-amber-700 text-xs mt-1">New Nevada residents must register their vehicles within <strong>30 days</strong> of establishing residency. Failure to do so results in late fees and potential traffic citations.</p>
        </div>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">What You Need</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: 'ri-file-text-line', title: 'Out-of-State Title', desc: 'Your current vehicle title from your previous state. If financed, your lender may hold the title.' },
              { icon: 'ri-shield-check-line', title: 'Nevada Insurance', desc: 'Must obtain Nevada insurance from a Nevada-licensed agent before registering.' },
              { icon: 'ri-leaf-line', title: 'Emissions Test', desc: 'Required for most vehicles in Clark County (Las Vegas). Must be done at an authorized Nevada station.' },
              { icon: 'ri-search-eye-line', title: 'VIN Inspection', desc: 'Required for all vehicles being registered in Nevada for the first time. Fee: $1.' },
              { icon: 'ri-id-card-line', title: 'Nevada Driver\'s License', desc: 'You should also obtain a Nevada driver\'s license within 30 days of establishing residency.' },
              { icon: 'ri-money-dollar-circle-line', title: 'Payment', desc: 'Registration fees, title transfer fee ($28.25), governmental services tax, and any other applicable fees.' },
            ].map(item => (
              <div key={item.title} className="flex items-start space-x-3 p-4 border border-obsidian-100 rounded-lg">
                <div className="w-8 h-8 flex items-center justify-center bg-gold-50 rounded-lg flex-shrink-0"><i className={`${item.icon} text-gold-600`}></i></div>
                <div><p className="font-semibold text-obsidian-900 text-sm">{item.title}</p><p className="text-obsidian-500 text-xs mt-0.5">{item.desc}</p></div>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">The Process</h2>
          <ol className="space-y-4">
            {[
              { step: '1', title: 'Get Nevada Insurance', desc: 'Contact a Nevada-licensed insurance agent to get coverage before you register.' },
              { step: '2', title: 'Pass Emissions Test', desc: 'Take your vehicle to an authorized smog station in Clark County.' },
              { step: '3', title: 'Get VIN Inspection', desc: 'Have a DMV representative or peace officer verify your VIN.' },
              { step: '4', title: 'Submit Registration Application', desc: 'Visit a DMV office or use Speedy DMV Services with all required documents.' },
              { step: '5', title: 'Pay All Fees', desc: 'Pay registration fees, title transfer fee, and governmental services tax.' },
              { step: '6', title: 'Receive Nevada Plates', desc: 'You\'ll receive Nevada license plates and a registration certificate.' },
            ].map(item => (
              <li key={item.step} className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-obsidian-900 text-gold-400 rounded-full font-bold text-sm flex-shrink-0">{item.step}</div>
                <div><p className="font-semibold text-obsidian-900 text-sm">{item.title}</p><p className="text-obsidian-500 text-xs mt-0.5">{item.desc}</p></div>
              </li>
            ))}
          </ol>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">New to Nevada? We Make It Easy</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services specializes in helping new Nevada residents get their vehicles registered quickly and correctly.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaNewResidentRegistrationPage;
